import { GlobalSettings } from "../global_settings.js";
import { fpsEventMarkers } from "../fps_event_markers.js";

// Self-contained FPS overlay: every RAF frame calls tick(now). Stores the last
// WINDOW_MS of raw per-frame timings in a ring buffer and renders a frame-time
// graph with percentiles, so both steady FPS and individual spikes are visible.
export class FpsOverlay
{
  private static canvas: HTMLCanvasElement | null = null;
  private static ctx: CanvasRenderingContext2D | null = null;

  private static readonly DISPLAY_W = 372;
  private static readonly DISPLAY_H = 252;
  private static readonly MAX_DPR = 1.5;

  private static graphWidth(): number
  {
    return this.DISPLAY_W - 16;
  }

  private static graphHeight(): number
  {
    return this.DISPLAY_H - 74 - 20;
  }
  private static readonly WINDOW_MS = 30_000;
  private static readonly REDRAW_INTERVAL_MS = 500;
  private static readonly CUR_FPS_WINDOW_MS = 1000;
  private static readonly LOG_INTERVAL_MS = 1000;
  private static readonly SPIKE_DT_MS = 50;
  private static readonly JANK_DT_MS = 25;

  private static readonly CAPACITY = 4096;
  private static frame_t = new Float64Array( FpsOverlay.CAPACITY );
  private static frame_dt = new Float32Array( FpsOverlay.CAPACITY );
  // Reused by render(): the window is rebuilt 4x a second over up to CAPACITY
  // frames, and allocating an object per frame plus a comparator sort there
  // made the overlay cause ~12 ms spikes of its own, growing as the window filled.
  private static win_t = new Float64Array( FpsOverlay.CAPACITY );
  private static win_dt = new Float32Array( FpsOverlay.CAPACITY );
  private static sort_buf = new Float32Array( FpsOverlay.CAPACITY );
  private static head = 0;
  private static count = 0;

  private static prev_frame_time = 0;
  private static prev_redraw_time = 0;
  private static graph_canvas: HTMLCanvasElement | null = null;
  private static graph_ctx: CanvasRenderingContext2D | null = null;
  private static graph_back_canvas: HTMLCanvasElement | null = null;
  private static graph_back_ctx: CanvasRenderingContext2D | null = null;
  private static prev_log_time = 0;

  private static current_screen_provider: ( () => string ) | null = null;

  // Per-frame section timings, so a spike can be attributed to physics, game
  // logic, 2D UI or the GL draw instead of just showing up as a long frame.
  private static section_phys = 0;
  private static section_game = 0;
  private static section_ui2d = 0;
  private static section_gl = 0;
  private static section_steps = 0;
  private static last_spike = "";

  private static readonly PROF_CAPACITY = 8192;
  private static prof_dt = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_phys = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_game = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_ui2d = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_gl = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_steps = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_balls = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_debris = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_maps = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_step = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_hook = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_bodies = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_colliders = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_awake = new Float32Array( FpsOverlay.PROF_CAPACITY );
  private static prof_ev: string[] = [];
  private static prof_n = 0;
  private static profiling = false;

  private static live_balls = 0;
  private static live_debris = 0;
  private static live_maps = 0;
  private static live_step = 0;
  private static live_hook = 0;
  private static live_bodies = 0;
  private static live_colliders = 0;
  private static live_awake = 0;

  public static markPhysics( step_ms: number, hook_ms: number, counts: { bodies: number; colliders: number; awake: number } ): void
  {
    this.live_step = step_ms;
    this.live_hook = hook_ms;
    this.live_bodies = counts.bodies;
    this.live_colliders = counts.colliders;
    this.live_awake = counts.awake;
  }

  public static markLive( balls: number, debris: number, maps = 0 ): void
  {
    this.live_balls = balls;
    this.live_debris = debris;
    this.live_maps = maps;
  }

  public static profileStart(): void
  {
    this.prof_n = 0;
    this.prof_ev = [];
    this.profiling = true;
  }

  public static profileStop(): void
  {
    this.profiling = false;
  }

  public static profileStats(): Record<string, unknown>
  {
    const n = Math.min( this.prof_n, this.PROF_CAPACITY );
    if ( n === 0 ) return { n: 0 };

    const pick = ( source: Float32Array ) =>
    {
      const values = Array.from( source.subarray( 0, n ) ).sort( ( a, b ) => a - b );
      const at = ( q: number ) => +values[Math.min( n - 1, Math.floor( n * q ) )].toFixed( 2 );
      const mean = +( values.reduce( ( a, b ) => a + b, 0 ) / n ).toFixed( 2 );
      return { mean, p50: at( 0.5 ), p90: at( 0.9 ), p99: at( 0.99 ), max: at( 0.999 ) };
    };

    const dt = pick( this.prof_dt );
    const phys = pick( this.prof_phys );
    const game = pick( this.prof_game );
    const ui2d = pick( this.prof_ui2d );
    const gl = pick( this.prof_gl );

    let js = 0;
    for ( let i = 0; i < n; i++ )
      js += this.prof_phys[i] + this.prof_game[i] + this.prof_ui2d[i] + this.prof_gl[i];

    const SPIKE_MS = 25;
    const spike_index: number[] = [];
    for ( let i = 0; i < n; i++ )
      if ( this.prof_dt[i] >= SPIKE_MS )
        spike_index.push( i );

    const spikeMean = ( source: Float32Array ) =>
      spike_index.length === 0
        ? 0
        : +( spike_index.reduce( ( sum, i ) => sum + source[i], 0 ) / spike_index.length ).toFixed( 2 );

    return {
      n,
      fps: +( 1000 * n / ( dt.mean * n ) ).toFixed( 2 ),
      dt, phys, game, ui2d, gl,
      steps: pick( this.prof_steps ),
      js_mean: +( js / n ).toFixed( 2 ),
      outside_mean: +( dt.mean - js / n ).toFixed( 2 ),
      worst: [...Array( n ).keys() ]
        .sort( ( a, b ) => this.prof_dt[b] - this.prof_dt[a] )
        .slice( 0, 10 )
        .map( i => ( {
          frame: i,
          dt: +this.prof_dt[i].toFixed( 1 ),
          phys: +this.prof_phys[i].toFixed( 1 ),
          game: +this.prof_game[i].toFixed( 1 ),
          ui2d: +this.prof_ui2d[i].toFixed( 1 ),
          gl: +this.prof_gl[i].toFixed( 1 ),
          steps: this.prof_steps[i],
          balls: this.prof_balls[i],
          debris: this.prof_debris[i],
          maps: this.prof_maps[i],
          step: +this.prof_step[i].toFixed( 1 ),
          hook: +this.prof_hook[i].toFixed( 1 ),
          bodies: this.prof_bodies[i],
          awake: this.prof_awake[i],
          new_map: i > 0 && this.prof_maps[i] > this.prof_maps[i - 1] ? 1 : 0,
          ev: this.prof_ev[i] ?? "none",
        } ) ),
      spike_events: spike_index.reduce( ( map: Record<string, number>, i ) =>
      {
        const key = this.prof_ev[i] ?? "none";
        map[key] = ( map[key] ?? 0 ) + 1;
        return map;
      }, {} ),
      balls: pick( this.prof_balls ),
      debris: pick( this.prof_debris ),
      step: pick( this.prof_step ),
      hook: pick( this.prof_hook ),
      bodies: pick( this.prof_bodies ),
      colliders: pick( this.prof_colliders ),
      awake: pick( this.prof_awake ),
      spikes: {
        count: spike_index.length,
        percent: +( 100 * spike_index.length / n ).toFixed( 1 ),
        dt: spikeMean( this.prof_dt ),
        phys: spikeMean( this.prof_phys ),
        game: spikeMean( this.prof_game ),
        ui2d: spikeMean( this.prof_ui2d ),
        gl: spikeMean( this.prof_gl ),
        steps: spikeMean( this.prof_steps ),
        step_ms: spikeMean( this.prof_step ),
        hook_ms: spikeMean( this.prof_hook ),
        bodies: spikeMean( this.prof_bodies ),
        outside: +( spikeMean( this.prof_dt )
          - spikeMean( this.prof_phys ) - spikeMean( this.prof_game )
          - spikeMean( this.prof_ui2d ) - spikeMean( this.prof_gl ) ).toFixed( 2 ),
      },
    };
  }

  /** Set by UiPerfProbe, which reads the sections without the overlay on. */
  public static force_marks = false;
  /** Set by PerfSession for the whole recording. */
  public static session_marks = false;

  public static lastSections(): { phys: number; game: number; ui2d: number; gl: number }
  {
    return { phys: this.section_phys, game: this.section_game, ui2d: this.section_ui2d, gl: this.section_gl };
  }

  public static mark( section: string, ms: number ): void
  {
    if ( !GlobalSettings.SHOW_FPS_OVERLAY && !this.profiling && !this.force_marks && !this.session_marks ) return;
    if ( section === "phys" ) this.section_phys = ms;
    else if ( section === "game" ) this.section_game = ms;
    else if ( section === "ui2d" ) this.section_ui2d = ms;
    else if ( section === "gl" ) this.section_gl = ms;
  }

  public static markSteps( steps: number ): void
  {
    this.section_steps = steps;
  }


  // Gameplay events that land inside a single frame, so a spike can be
  // correlated with what actually happened (a block shattering, a shot fired).
  private static event_counts: Record<string, number> = {};

  public static noteEvent( name: string ): void
  {
    this.event_counts[name] = ( this.event_counts[name] ?? 0 ) + 1;
  }

  private static takeEvents(): string
  {
    const parts: string[] = [];
    for ( const key of Object.keys( this.event_counts ) )
    {
      if ( this.event_counts[key] > 0 )
        parts.push( `${key}:${this.event_counts[key]}` );
      this.event_counts[key] = 0;
    }
    return parts.length > 0 ? parts.join( "," ) : "none";
  }

  public static setCurrentScreenProvider( fn: () => string ): void
  {
    this.current_screen_provider = fn;
  }

  public static init(): void
  {
    this.canvas = document.getElementById( "fpsCanvas" ) as HTMLCanvasElement | null;
    if ( !this.canvas ) return;

    const dpr = Math.min( window.devicePixelRatio || 1, this.MAX_DPR );
    this.canvas.width = Math.round( this.DISPLAY_W * dpr );
    this.canvas.height = Math.round( this.DISPLAY_H * dpr );
    this.canvas.style.width = `${this.DISPLAY_W}px`;
    this.canvas.style.height = `${this.DISPLAY_H}px`;
    this.canvas.style.display = GlobalSettings.SHOW_FPS_OVERLAY ? "block" : "none";

    this.ctx = this.canvas.getContext( "2d" );
    this.ctx?.setTransform( 1, 0, 0, 1, 0, 0 );
    this.ctx?.scale( dpr, dpr );

    const makeGraphCanvas = (): [ HTMLCanvasElement, CanvasRenderingContext2D | null ] =>
    {
      const c = document.createElement( "canvas" );
      c.width = Math.round( this.graphWidth() * dpr );
      c.height = Math.round( this.graphHeight() * dpr );
      const cctx = c.getContext( "2d" );
      cctx?.setTransform( 1, 0, 0, 1, 0, 0 );
      cctx?.scale( dpr, dpr );
      return [ c, cctx ];
    };
    [ this.graph_canvas, this.graph_ctx ] = makeGraphCanvas();
    [ this.graph_back_canvas, this.graph_back_ctx ] = makeGraphCanvas();
    this.graph_next_t = 0;
    this.graph_prev_avg_y = Number.NaN;
    this.graph_prev_worst_y = Number.NaN;

    const now = performance.now();
    this.prev_frame_time = now;
    this.prev_redraw_time = now;
    this.prev_log_time = now;
  }

  public static setEnabled( enabled: boolean ): void
  {
    GlobalSettings.SHOW_FPS_OVERLAY = enabled;
    try
    {
      if ( enabled ) localStorage.setItem( GlobalSettings.SHOW_FPS_OVERLAY_KEY, "1" );
      else localStorage.removeItem( GlobalSettings.SHOW_FPS_OVERLAY_KEY );
    }
    catch ( e ) { /* storage unavailable */ }
    if ( this.canvas )
    {
      this.canvas.style.display = enabled ? "block" : "none";
      if ( !enabled && this.ctx )
        this.ctx.clearRect( 0, 0, this.DISPLAY_W, this.DISPLAY_H );
    }
  }

  public static toggle(): void { this.setEnabled( !GlobalSettings.SHOW_FPS_OVERLAY ); }
  public static isVisible(): boolean { return GlobalSettings.SHOW_FPS_OVERLAY; }

  public static tick( now: number ): void
  {
    const overlay = GlobalSettings.SHOW_FPS_OVERLAY;
    const events = overlay || this.profiling ? this.takeEvents() : "none";

    if ( this.profiling )
    {
      const frame_dt = now - this.prev_frame_time;

      if ( frame_dt > 0 && frame_dt < 4000 && this.prof_n < this.PROF_CAPACITY )
      {
        const i = this.prof_n++;
        this.prof_dt[i] = frame_dt;
        this.prof_phys[i] = this.section_phys;
        this.prof_game[i] = this.section_game;
        this.prof_ui2d[i] = this.section_ui2d;
        this.prof_gl[i] = this.section_gl;
        this.prof_steps[i] = this.section_steps;
        this.prof_ev[i] = events;
        this.prof_balls[i] = this.live_balls;
        this.prof_debris[i] = this.live_debris;
        this.prof_maps[i] = this.live_maps;
        this.prof_step[i] = this.live_step;
        this.prof_hook[i] = this.live_hook;
        this.prof_bodies[i] = this.live_bodies;
        this.prof_colliders[i] = this.live_colliders;
        this.prof_awake[i] = this.live_awake;
      }
    }

    if ( !overlay )
    {
      this.prev_frame_time = now;
      return;
    }

    const dt = now - this.prev_frame_time;
    this.prev_frame_time = now;
    if ( dt <= 0 || dt > 4000 ) return;

    if ( dt >= this.SPIKE_DT_MS )
    {
      const js = this.section_phys + this.section_game + this.section_ui2d + this.section_gl;
      this.last_spike =
        `${dt.toFixed( 0 )}ms phys${this.section_phys.toFixed( 0 )}`
        + ` game${this.section_game.toFixed( 0 )} ui${this.section_ui2d.toFixed( 0 )}`
        + ` gl${this.section_gl.toFixed( 0 )} out${Math.max( 0, dt - js ).toFixed( 0 )}`
        + ` x${this.section_steps}`;

      console.log( `[SPIKE] ${this.last_spike} ev=${events}` );
    }

    this.frame_t[this.head] = now;
    this.frame_dt[this.head] = dt;
    this.head = ( this.head + 1 ) % this.CAPACITY;
    if ( this.count < this.CAPACITY ) this.count++;

    if ( now - this.prev_redraw_time >= this.REDRAW_INTERVAL_MS )
    {
      this.prev_redraw_time = now;
      this.render( now );
    }
  }

  // Copies the frames inside WINDOW_MS into win_t / win_dt, oldest first, and
  // returns how many there are.
  private static collectWindow( now: number ): number
  {
    const from = now - this.WINDOW_MS;
    let n = 0;
    while ( n < this.count )
    {
      const idx = ( this.head - 1 - n + this.CAPACITY * 2 ) % this.CAPACITY;
      if ( this.frame_t[idx] < from ) break;
      n++;
    }

    for ( let k = 0; k < n; k++ )
    {
      const idx = ( this.head - n + k + this.CAPACITY * 2 ) % this.CAPACITY;
      this.win_t[k] = this.frame_t[idx];
      this.win_dt[k] = this.frame_dt[idx];
    }
    return n;
  }

  private static percentileDt( sorted: ArrayLike<number>, p: number ): number
  {
    if ( sorted.length === 0 ) return 0;
    const idx = Math.min( sorted.length - 1, Math.floor( ( sorted.length - 1 ) * p ) );
    return sorted[idx];
  }

  private static render( now: number ): void
  {
    if ( !this.ctx || !this.canvas ) return;

    const n = this.collectWindow( now );
    if ( n < 2 ) return;

    // Typed-array sort is numeric and allocation-free.
    const dts = this.sort_buf.subarray( 0, n );
    dts.set( this.win_dt.subarray( 0, n ) );
    dts.sort();
    const window_span = this.win_t[n - 1] - this.win_t[0];
    const avg_fps = window_span > 0 ? ( n - 1 ) / ( window_span / 1000 ) : 0;

    let recent_span = 0;
    let recent_frames = 0;
    for ( let i = n - 1; i > 0 && recent_span < this.CUR_FPS_WINDOW_MS; i-- )
    {
      recent_span += this.win_dt[i];
      recent_frames++;
    }
    const cur_fps = recent_span > 0 ? recent_frames / ( recent_span / 1000 ) : 0;

    const p50 = this.percentileDt( dts, 0.50 );
    const p95 = this.percentileDt( dts, 0.95 );
    const p99 = this.percentileDt( dts, 0.99 );
    const worst = dts[dts.length - 1];
    const low1 = p99 > 0 ? 1000 / p99 : 0;
    let spikes = 0;
    let jank = 0;
    for ( const d of dts )
    {
      if ( d >= this.SPIKE_DT_MS ) spikes++;
      if ( d >= this.JANK_DT_MS ) jank++;
    }
    const jank_pct = ( jank / dts.length ) * 100;

    if ( now - this.prev_log_time >= this.LOG_INTERVAL_MS )
    {
      this.prev_log_time = now;
      console.log(
        `🚀 FPS cur=${cur_fps.toFixed( 1 )} avg=${avg_fps.toFixed( 1 )} 1%low=${low1.toFixed( 1 )}`
        + ` | frame p50=${p50.toFixed( 1 )}ms p95=${p95.toFixed( 1 )}ms p99=${p99.toFixed( 1 )}ms`
        + ` worst=${worst.toFixed( 0 )}ms spikes=${spikes} jank=${jank_pct.toFixed( 0 )}%`
      );
    }

    const ctx = this.ctx;
    const w = this.DISPLAY_W;
    const h = this.DISPLAY_H;

    ctx.clearRect( 0, 0, w, h );
    ctx.fillStyle = "rgba(0, 0, 0, 0.62)";
    ctx.fillRect( 0, 0, w, h );

    ctx.font = "13px monospace";
    ctx.textAlign = "left";

    ctx.fillStyle = cur_fps >= 50 ? "#00FF00" : cur_fps >= 30 ? "#FFD000" : "#FF5050";
    ctx.fillText( `FPS ${cur_fps.toFixed( 1 )}`, 8, 17 );
    ctx.fillStyle = "#A0FFA0";
    ctx.fillText( `avg ${avg_fps.toFixed( 1 )}`, 92, 17 );
    ctx.fillStyle = "#FF9090";
    ctx.fillText( `1% low ${low1.toFixed( 1 )}`, 172, 17 );
    ctx.fillStyle = "#00FFFF";
    ctx.fillText( `${this.current_screen_provider?.() ?? "?"}`, 286, 17 );

    ctx.fillStyle = "#C0C0FF";
    ctx.fillText( `frame ms  p50 ${p50.toFixed( 1 )}   p95 ${p95.toFixed( 1 )}   p99 ${p99.toFixed( 1 )}`, 8, 34 );
    ctx.fillStyle = spikes > 0 ? "#FF7070" : "#909090";
    ctx.fillText( `worst ${worst.toFixed( 0)}ms   spikes(>${this.SPIKE_DT_MS}ms) ${spikes}   jank(>${this.JANK_DT_MS}ms) ${jank_pct.toFixed( 0 )}%`, 8, 51 );

    ctx.fillStyle = "#FFD060";
    ctx.font = "11px monospace";
    ctx.fillText(
      this.last_spike ? `last spike ${this.last_spike}` : "no spike yet",
      8, 67
    );

    if ( this.graph_canvas )
    {
      const gx = 8;
      const gy = 74;
      const gw = this.graphWidth();
      const gh = this.graphHeight();
      this.advanceGraph( now );
      ctx.fillStyle = "rgba(255,255,255,0.05)";
      ctx.fillRect( gx, gy, gw, gh );
      ctx.drawImage( this.graph_canvas, gx, gy, gw, gh );
      ctx.font = "9px monospace";
      for ( const guide of [ 60, 45, 30, 15 ] )
      {
        const y = gy + this.graphY( guide );
        ctx.strokeStyle = guide === 60 ? "rgba(255,0,255,0.5)" : "rgba(255,255,255,0.15)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo( gx, y );
        ctx.lineTo( gx + gw, y );
        ctx.stroke();
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        ctx.fillText( `${guide}`, gx + gw - 16, y - 2 );
      }
    }

    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.font = "10px monospace";
    ctx.fillText( `out = time outside JS (GPU/GC/compositor), x = physics steps`, 8, h - 6 );
  }

  private static graph_next_t = 0;
  private static graph_prev_avg_y = Number.NaN;
  private static graph_prev_worst_y = Number.NaN;
  private static readonly GRAPH_MAX_FPS = 62;

  private static graphY( fps: number ): number
  {
    const h = this.graphHeight();
    return h - ( Math.max( 0, Math.min( this.GRAPH_MAX_FPS, fps ) ) / this.GRAPH_MAX_FPS ) * h;
  }

  private static advanceGraph( now: number ): void
  {
    let ctx = this.graph_ctx;
    if ( !ctx || !this.graph_canvas || !this.graph_back_ctx || !this.graph_back_canvas ) return;

    const w = this.graphWidth();
    const h = this.graphHeight();
    const col_ms = this.WINDOW_MS / w;

    if ( this.graph_next_t === 0 )
    {
      this.graph_next_t = now - col_ms;
      return;
    }

    let k = Math.floor( ( now - this.graph_next_t ) / col_ms );
    if ( k <= 0 ) return;
    if ( k > w )
    {
      k = w;
      this.graph_next_t = now - k * col_ms;
      this.graph_prev_avg_y = Number.NaN;
      this.graph_prev_worst_y = Number.NaN;
    }

    const col_time = new Float32Array( k );
    const col_frames = new Int32Array( k );
    const col_max_dt = new Float32Array( k );
    for ( let n = 0; n < this.count; n++ )
    {
      const idx = ( this.head - 1 - n + this.CAPACITY * 2 ) % this.CAPACITY;
      const t = this.frame_t[idx];
      if ( t < this.graph_next_t ) break;
      const col = Math.floor( ( t - this.graph_next_t ) / col_ms );
      if ( col >= k ) continue;
      const dt = this.frame_dt[idx];
      col_time[col] += dt;
      col_frames[col]++;
      if ( dt > col_max_dt[col] ) col_max_dt[col] = dt;
    }

    const back_ctx = this.graph_back_ctx;
    back_ctx.clearRect( 0, 0, w, h );
    back_ctx.drawImage( this.graph_canvas, -k, 0, w, h );
    [ this.graph_canvas, this.graph_back_canvas ] = [ this.graph_back_canvas, this.graph_canvas ];
    [ this.graph_ctx, this.graph_back_ctx ] = [ back_ctx, ctx ];
    ctx = back_ctx;

    const drawSeries = ( color: string, width: number, value: ( c: number ) => number, prev_key: "graph_prev_avg_y" | "graph_prev_worst_y" ) =>
    {
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.beginPath();
      let prev_x = w - k - 0.5;
      let prev_y = this[prev_key];
      let started = false;
      for ( let c = 0; c < k; c++ )
      {
        if ( col_frames[c] === 0 ) continue;
        const px = w - k + c + 0.5;
        const py = this.graphY( value( c ) );
        if ( Number.isFinite( prev_y ) )
        {
          if ( !started ) { ctx.moveTo( prev_x, prev_y ); started = true; }
          ctx.lineTo( px, py );
        }
        prev_x = px;
        prev_y = py;
      }
      if ( started ) ctx.stroke();
      this[prev_key] = prev_y;
    };

    drawSeries( "rgba(255,80,80,0.9)", 1, c => 1000 / col_max_dt[c], "graph_prev_worst_y" );
    drawSeries( "rgba(0,255,0,0.9)", 1.5, c => col_frames[c] * 1000 / col_time[c], "graph_prev_avg_y" );

    ctx.strokeStyle = "rgba(255,40,40,0.9)";
    ctx.lineWidth = 2;
    for ( let c = 0; c < k; c++ )
    {
      if ( col_frames[c] === 0 || col_max_dt[c] < this.SPIKE_DT_MS ) continue;
      const px = w - k + c + 0.5;
      ctx.beginPath();
      ctx.moveTo( px, 0 );
      ctx.lineTo( px, 8 );
      ctx.stroke();
    }

    const span_end = this.graph_next_t + k * col_ms;
    fpsEventMarkers.clearOldMarkers( now - this.WINDOW_MS );
    for ( const marker of fpsEventMarkers.getMarkers() )
    {
      if ( marker.timestamp < this.graph_next_t || marker.timestamp >= span_end ) continue;
      const mx = w - k + ( marker.timestamp - this.graph_next_t ) / col_ms;
      ctx.strokeStyle = marker.color;
      ctx.lineWidth = 1.5;
      ctx.setLineDash( [ 4, 4 ] );
      ctx.beginPath();
      ctx.moveTo( mx, 0 );
      ctx.lineTo( mx, h );
      ctx.stroke();
      ctx.setLineDash( [] );
      ctx.font = "8px monospace";
      ctx.fillStyle = marker.color;
      ctx.textAlign = "center";
      ctx.fillText( marker.label, mx, 16 );
      ctx.textAlign = "left";
    }

    this.graph_next_t = span_end;
  }
}
